import { omit } from 'lodash';

import { getStateSingleAutocompleteInitialValues } from '@occc-app/design-system';
import {
  RetailerNetworkCustomerFilter,
  RetailerNetworkCustomerFragment,
  RetailerNetworkFragment,
} from '@occc/graphql';

import {
  RetailerNetworkConfigurationFormInitialValues,
  RetailerNetworkConfigurationFormValues,
  RetailerNetworkCustomerAutocompleteItem,
} from './retailer-network-configuration.types';

import { useGetRetailerNetworkCustomerLazy } from '../../data-access/queries';

export const mapRetailerNetworkCustomerQueryResponseToRawArray = (
  response: ReturnType<typeof useGetRetailerNetworkCustomerLazy>['response'],
): RetailerNetworkCustomerAutocompleteItem[] =>
  response.data
    ? response.data.map(({ id, uuid, name }) => ({
        id,
        uuid,
        name,
      }))
    : [];

export const makeRetailerNetworkCustomerFilter = (
  value?: string,
): RetailerNetworkCustomerFilter => ({
  search: value ? value : undefined,
});

export const localizeNumber = (n: number): string => {
  return n.toLocaleString();
};

export const calculateExpectedGoalOutcome = (
  numLocations?: number,
  monthlyGoalValue?: number,
): string => {
  if (!numLocations || !monthlyGoalValue) {
    return '0';
  }
  return localizeNumber(numLocations * monthlyGoalValue);
};

export const getInputProps = (inputName: string) => ({
  'data-testid': `retailer-network-form-field-${inputName}`,
});

export const getInitialValues = (
  retailerNetwork?: RetailerNetworkFragment,
): RetailerNetworkConfigurationFormInitialValues => {
  const defaultInitialValues = {
    name: '',
    displayName: '',
    customerId: undefined,
    adminName: '',
    address: {
      street1: '',
      city: '',
      state: undefined,
      zipCode: '',
    },
    phoneNumber: '',
    emailAddress: '',
    monthlyPerformanceGoal: {
      membersPerStore: 0,
      transactionsPerStore: 0,
    },
  };

  if (retailerNetwork) {
    const retailerNetworkDetails = omit(retailerNetwork, [
      '__typename',
      'numberOfLocations',
      'numberOfLocationGroups',
    ]);
    const retailerNetworkAddress = omit(retailerNetwork.address, [
      '__typename',
      'uuid',
      'alias',
      'street2',
    ]);
    const retailerNetworkMonthlyPerformanceGoal = omit(
      retailerNetwork.monthlyPerformanceGoal,
      '__typename',
    );

    return {
      ...retailerNetworkDetails,
      customerId: {
        uuid: retailerNetworkDetails.customerId, // This is needed to prefill customerId autocomplete
        id: retailerNetworkDetails.customerId, // This is needed to prefill customerId autocomplete
        name: retailerNetworkDetails.customerId, // This is needed to prefill customerId autocomplete
      },
      address: {
        ...retailerNetworkAddress,
        state: getStateSingleAutocompleteInitialValues(retailerNetwork.address.state),
      },
      monthlyPerformanceGoal: {
        ...retailerNetworkMonthlyPerformanceGoal,
      },
    };
  }

  return defaultInitialValues;
};

export const getRetailerNetworkConfigurationUpdateInput = (
  values: RetailerNetworkConfigurationFormValues,
) => {
  return {
    ...omit(values, 'customerId'),
    address: {
      ...values.address,
      state: values.address.state.id,
    },
  };
};
